import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { supabase } from '../../lib/supabase';
import { useCurrentUser } from '../../lib/currentUser';

const Header = ({ onMenuToggle, isMenuOpen = false }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const profileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const currentUser = useCurrentUser();

  // NOTE: the notification bell used to live here. It was removed because
  // (a) the real notifications feed isn't implemented, so the dropdown
  // was always empty, and (b) it was a floating clickable bell icon
  // that added visual noise + tap ambiguity for older or lower-tech
  // users. We can add it back the day we have a real feed to push.

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef?.current && !profileRef?.current?.contains(event?.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      // Search wiring isn't in place yet — no-op for now rather than shipping
      // a fake result set. See Dashboard/StudentSearchBar for real search.
    }
  };

  const handleProfileAction = async (action) => {
    setIsProfileOpen(false);
    switch (action) {
      case 'profile':
      case 'preferences':
        navigate('/preferences');
        return;
      case 'help':
        navigate('/help');
        return;
      case 'logout':
        try {
          if (supabase) {
            await supabase.auth.signOut();
          }
        } catch (err) {
          // Sign-out failed — the /login route will still let a fresh
          // sign-in attempt through, so we don't block navigation on it.
          console.error('Sign out error:', err);
        }
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem('userSession');
          }
        } catch {
          // ignore
        }
        navigate('/login');
        return;
      default:
        return;
    }
  };

  const displayName = currentUser?.name || 'Signed-in user';
  const displayRole = currentUser?.roleLabel || '';
  const displayEmail = currentUser?.email || '';
  const displayInitials = currentUser?.initials || '?';

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-surface border-b border-border z-[1000] shadow-soft">
      <div className="flex items-center justify-between h-full px-6">
        {/* Left Section - Menu Toggle & Search */}
        <div className="flex items-center gap-4 flex-1">
          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="lg:hidden"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? 'X' : 'Menu'} size={20} />
          </Button>

          {/* Global Search */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center max-w-md">
            <div className="relative w-full">
              <Icon
                name="Search"
                size={16}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              />
              {/* This header search is a placeholder — real search
                  lives on the Students page (StudentSearchBar). The
                  input stays visually because it's a strong signal
                  "there IS search in this app", but is disabled to
                  avoid the confusion of typing here and getting
                  nothing back. */}
              <input
                type="text"
                placeholder="Search students on the Students page"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                disabled
                aria-label="Search — available on the Students page"
                className="w-full pl-10 pr-4 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200 cursor-not-allowed opacity-70"
              />
            </div>
          </form>
        </div>

        {/* Right Section - Notifications & Profile */}
        <div className="flex items-center gap-2">
          {/* User Profile — the ONLY thing on the right side of the header
              now. Bigger tap target (h-12), name shown on every viewport
              (not just sm+), and a visible chevron so it's obvious this
              is a menu. */}
          <div className="relative" ref={profileRef}>
            <Button
              variant="ghost"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2 px-2 py-1.5 h-12 min-h-12"
              aria-haspopup="menu"
              aria-expanded={isProfileOpen}
              aria-label={`Account menu for ${displayName}`}
            >
              <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-semibold text-base">
                {displayInitials}
              </div>
              <div className="hidden xs:block text-left">
                <p className="text-sm font-medium text-foreground leading-tight">{displayName}</p>
                {displayRole && (
                  <p className="text-xs text-muted-foreground leading-tight">{displayRole}</p>
                )}
              </div>
              <Icon name="ChevronDown" size={18} className="text-muted-foreground" />
            </Button>

            {/* Profile Dropdown. z-[900] keeps it under any open modal
                (z-[1100]) — so if a teacher tapped the profile chevron
                and then opened Collect Data, the dropdown is guaranteed
                to disappear behind the modal instead of intercepting
                taps through it. Same rule for the sidebar (z-[900]). */}
            {isProfileOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-elevated z-[900] animate-fade-in">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                      {displayInitials}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-popover-foreground truncate">
                        {displayName}
                      </p>
                      {displayRole && (
                        <p className="text-sm text-muted-foreground truncate">
                          {displayRole}
                        </p>
                      )}
                      {displayEmail && (
                        <p className="text-xs text-muted-foreground truncate">
                          {displayEmail}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleProfileAction('profile')}
                    className="w-full justify-start gap-2"
                  >
                    <Icon name="User" size={16} />
                    Profile Settings
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleProfileAction('preferences')}
                    className="w-full justify-start gap-2"
                  >
                    <Icon name="Settings" size={16} />
                    Preferences
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleProfileAction('help')}
                    className="w-full justify-start gap-2"
                  >
                    <Icon name="HelpCircle" size={16} />
                    Help & Support
                  </Button>
                  <div className="border-t border-border my-2" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleProfileAction('logout')}
                    className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                  >
                    <Icon name="LogOut" size={16} />
                    Sign Out
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
